import React from "react";
import { ArrowRight, CalendarDays, Home, Building2, ChevronDown, User, Phone, } from "lucide-react";
import { toast } from "sonner";
import LocationMap from "../components/LocationMap";
import LocationAutocomplete from "../components/LocationAutocomplete";
export default function Step1({ data, setData, next, }) {
  const types = [
    [
      "Residential Move",
      "Home, Apartment, Villa",
      Home,
    ],
    [
      "Commercial Move",
      "Office, Shop, Business",
      Building2,
    ]
  ];
  const getTodayDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");
    const day = String(
      date.getDate()
    ).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const today = getTodayDate();
  const updateData = (key, value) => {
    setData((prev) => ({ ...prev, [key]: value, }));
  };
  const handlePickupChange = (address) => {
    setData((prev) => ({ ...prev, from: address, }));
  };
  const handlePickupLocationSelect = (location) => {
    setData((prev) => ({ ...prev, fromLocation: location, }));
  };
  const handleDeliveryChange = (address) => {
    setData((prev) => ({ ...prev, to: address, }));
  };
  const handleDeliveryLocationSelect = (location) => {
    setData((prev) => ({ ...prev, toLocation: location, }));
  };
  const handleNext = () => {
    const pickup = String(data?.from || "").trim();
    if (!pickup) {
      toast.error("Pickup location required", {
        description: "Please enter your pickup location.",
      });
      return;
    }
    const delivery = String(data?.to || "").trim();
    if (!delivery) {
      toast.error("Delivery location required", {
        description: "Please enter your delivery location.",
      });
      return;
    }
    const requiredFields = [
      {
        key: "moveType",
        label: "Moving Type",
      },
      {
        key: "moveSubType",
        label: "Move Type",
      },
      {
        key: "name",
        label: "Full Name",
      },
      {
        key: "phone",
        label: "Mobile Number",
      },
      {
        key: "date",
        label: "Moving Date",
      },
      {
        key: "houseType",
        label: "House Type",
      },
      {
        key: "currentFloor",
        label: "Current Floor",
      },
      {
        key: "liftAvailable",
        label: "Lift Available",
      },
    ];
    const missingField = requiredFields.find((field) => {
      const value = data?.[field.key];
      return (!value || String(value).trim() === "");
    });
    if (missingField) {
      toast.error("Please complete all required fields",
        {
          description: `${missingField.label} is required to continue.`,
        });
      return;
    }
    const phone = String(data.phone || "").replace(/\D/g, "");
    if (!/^[6-9]\d{9}$/.test(phone)) {
      toast.error("Invalid mobile number", {
        description: "Please enter a valid 10-digit mobile number.",
      });
      return;
    }
    if (data.date < today) {
      toast.error("Invalid moving date", {
        description: "Moving date cannot be in the past.",
      });
      return;
    }
    toast.success("Move information completed",
      {
        description: "Let's continue with your inventory.",
      });
    next();
  };
  return (
    <div className="page-grid">
      <main className="form-card">
        <h2>1. Move Information</h2>
        <p className="section-subtitle">Tell us about your move to get started.</p>
        <h4>Moving Type{" "}<span className="required">*</span></h4>
        <div className="choice-grid">
          {types.map(([title, sub, Icon]) => (
            <button key={title} type="button" className={"choice-card " + (data.moveType === title ? "selected" : "")}
              onClick={() => updateData("moveType", title)}>
              <Icon size={26} />
              <strong>{title}</strong>
              <span>{sub}</span>
              {data.moveType === title && (<b>✓</b>)}
            </button>
          ))}
        </div>
        <h4 className="subheading"style={{marginTop:20}}>Move Type{" "}<span className="required">*</span></h4>
        <div className="subtype-grid">
          <button type="button" className={"subtype-card " + (data.moveSubType === "Inter City" ? "selected" : "")}
            onClick={() => updateData("moveSubType", "Inter City")}>
            <div className="subtype-content">
              <strong>Inter City</strong>
              <span>Dedicated vehicle for your complete move</span>
            </div>
            {data.moveSubType === "Inter City" && (<b>✓</b>)}
          </button>
          <button type="button" className={"subtype-card " + (data.moveSubType === "Outer City" ? "selected" : "")}
            onClick={() => updateData("moveSubType", "Outer City")}>
            <div className="subtype-content">
              <strong>Outer City</strong>
              <span>Dedicated vehicle for your complete move</span>
            </div>
            {data.moveSubType === "Outer City" && (<b>✓</b>)}
          </button>
          <button type="button" className={"subtype-card " + (data.moveSubType === "Part Load" ? "selected" : "")}
            onClick={() => updateData("moveSubType", "Part Load")}>
            <div className="subtype-content">
              <strong>Part Load</strong>
              <span>Share vehicle space withother moves</span>
            </div>
            {data.moveSubType === "Part Load" && (<b>✓</b>)}
          </button>
        </div>
        <div className="field-grid two">
          <label>Moving From{" "}<span className="required">*</span>
            <LocationAutocomplete value={data.from || ""} placeholder="Search pickup location"
              onChange={handlePickupChange}
              onLocationSelect={handlePickupLocationSelect} />
          </label>
          <label> Moving To{" "}<span className="required">*</span>
            <LocationAutocomplete value={data.to || ""} placeholder="Search delivery location"
              onChange={handleDeliveryChange}
              onLocationSelect={handleDeliveryLocationSelect} />
          </label>
        </div>
        <LocationMap
          pickupLocation={data.fromLocation}
          deliveryLocation={data.toLocation} />
        <div className="field-grid two">
          <label> Full Name{" "}<span className="required">*</span>
            <div className="input-icon">
              <User size={18} />
              <input type="text" placeholder="Enter your full name" value={data.name || ""}
                onChange={(e) => updateData("name", e.target.value)} />
            </div>
          </label>
          <label>Mobile Number{" "}<span className="required">*</span>
            <div className="input-icon">
              <Phone size={18} />
              <input type="tel" inputMode="numeric" maxLength={10} placeholder="Enter 10-digit mobile number" value={data.phone || ""}
                onChange={(e) =>
                  updateData("phone", e.target.value.replace(/\D/g, ""))} />
            </div>
          </label>
        </div>
        <div className="field-grid four">
          <label>Moving Date{" "}<span className="required">*</span>
            <div className="input-icon">
              <CalendarDays size={18} />
              <input type="date" min={today} value={data.date || ""}
                onChange={(e) => updateData("date", e.target.value)} />
            </div>
          </label>
          <label>House Type{" "}<span className="required">*</span>
            <div className="input-icon select-wrapper">
              <Home size={18} />
              <select value={data.houseType || ""}
                onChange={(e) => updateData("houseType", e.target.value)}>
                <option value="">Select house type</option>
                <option value="1 BHK Apartment">1 BHK Apartment</option>
                <option value="2 BHK Apartment">2 BHK Apartment</option>
                <option value="3 BHK Apartment">3 BHK Apartment</option>
                <option value="Individual House">Individual House</option>
              </select>
              <ChevronDown size={16} />
            </div>
          </label>
          <label>
            Current Floor{" "}
            <span className="required">*</span>
            <div className="input-icon select-wrapper">
              <Home size={18} />
              <select value={data.currentFloor || ""}
                onChange={(e) =>
                  updateData("currentFloor", e.target.value)}>
                <option value="">Select floor</option>
                <option value="Ground Floor">Ground Floor</option>
                <option value="1st Floor">1st Floor</option>
                <option value="2nd Floor">2nd Floor</option>
                <option value="3rd Floor">3rd Floor</option>
                <option value="4th Floor">4th Floor</option>
                <option value="5th Floor">5th Floor</option>
                <option value="6th Floor">6th Floor</option>
                <option value="7th Floor">7th Floor</option>
                <option value="8th Floor">8th Floor</option>
                <option value="9th Floor">9th Floor</option>
                <option value="10th Floor">10th Floor</option>
              </select>
              <ChevronDown size={16} />
            </div>
          </label>
          <label>
            Lift Available{" "}
            <span className="required">*</span>
            <div className="radio-line">
              <label>
                <input type="radio" name="lift" value="Yes" checked={data.liftAvailable === "Yes"}
                  onChange={(e) => updateData("liftAvailable", e.target.value)} />
                Yes</label>
              <label>
                <input type="radio" name="lift" value="No" checked={data.liftAvailable === "No"}
                  onChange={(e) => updateData("liftAvailable", e.target.value)} />
                No</label>
            </div>
          </label>
        </div>
        <div className="actions">
          <button type="button" className="back ghost" disabled>← Back</button>
          <button type="button" className="primary" onClick={handleNext}>Next: Items & Inventory<ArrowRight size={18} /></button>
        </div>
      </main>
    </div>
  );
}